import styled from "styled-components";
import Text from "common/Typography/Text";
import { BasicBotData, BotType, LanguageNameSpaces } from "@shared/types";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import NextOrder from "features/bots/MyBots/NextOrder";
import Flex from "common/Boxes/Flex";
import Divider from "common/misc/Divider";
import { OrderTypes } from "@shared/features/trades/common/types";
import { typography } from "@shared/common/Themes/typography";
import { ActiveTradePreviewStyles } from "@shared/features/bots/styles";
import { useActiveTradePreview } from "@shared/features/bots/hooks";
import {
  BottomRowWrapper,
  StatisticTitle,
  StatisticWrapper,
} from "features/bots/MyBots/common/styles";
import { useContext } from "react";
import { CardsLayoutContext } from "features/bots/BotForm/common/CardsLayoutProvider";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_SEARCH } from "common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface ActiveTradePreviewProps {
  botData: BasicBotData;
}

const ActiveTradePreview = ({ botData }: ActiveTradePreviewProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { estimatedCardSize } = useContext(CardsLayoutContext)!;
  const {
    useVerticalStatistics,
    useActiveTradeRows,
    lastOrderData,
    noActiveTradeText,
    dividerText,
    activeTrade,
  } = useActiveTradePreview(botData, estimatedCardSize);
  const { next_stop, next_entry, next_take_profit } = activeTrade || {};
  const isGridBot = botData.bot_type === BotType.GRID;
  const symbol = botData?.symbols?.[0];
  const hideBorder = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);

  const getLastEntryFilled = () => {
    return (
      <Flex $flexDirection={useVerticalStatistics ? "column" : "row"}>
        {lastOrderData.texts[0]}
        <Text
          $size={typography.fontSizes.S}
          style={{
            display: "inline",
            marginLeft: useVerticalStatistics ? 0 : 10,
          }}
        >
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {lastOrderData.texts[1]}
        </Text>
      </Flex>
    );
  };

  const statisticProps = {
    $useActiveTradeRows: useActiveTradeRows,
    $hideBorder: hideBorder,
  };

  const getActiveTradeInfo = () => {
    return (
      <BottomRowWrapper $useActiveTradeRows={useActiveTradeRows}>
        {lastOrderData.shouldDisplay && (
          <StatisticWrapper {...statisticProps}>
            <StatisticTitle>{lastOrderData.title}</StatisticTitle>
            <StyledValue>{getLastEntryFilled()}</StyledValue>
          </StatisticWrapper>
        )}
        {next_entry && (
          <StatisticWrapper {...statisticProps}>
            <StatisticTitle>{t("labels.next_entry")}</StatisticTitle>
            <NextOrder
              symbol={symbol}
              exchange={botData.exchange}
              nextOrder={next_entry}
              orderType={OrderTypes.ENTRY}
              isMobile={useVerticalStatistics}
            />
          </StatisticWrapper>
        )}
        {next_stop && !next_entry && (
          <StatisticWrapper {...statisticProps}>
            <StatisticTitle>{t("labels.stop")}</StatisticTitle>
            <NextOrder
              symbol={symbol}
              exchange={botData.exchange}
              nextOrder={next_stop}
              orderType={OrderTypes.STOP}
              isMobile={useVerticalStatistics}
            />
          </StatisticWrapper>
        )}
        <StatisticWrapper {...statisticProps}>
          <StatisticTitle>
            {isGridBot ? t("labels.next_take_profit") : t("labels.take_profit")}
          </StatisticTitle>
          <NextOrder
            symbol={symbol}
            exchange={botData.exchange}
            nextOrder={next_take_profit}
            orderType={OrderTypes.TAKE_PROFIT}
            isMobile={useVerticalStatistics}
          />
        </StatisticWrapper>
      </BottomRowWrapper>
    );
  };

  return (
    <>
      <Divider text={dividerText} />
      {activeTrade ? (
        getActiveTradeInfo()
      ) : (
        <NoTradeText>{noActiveTradeText}</NoTradeText>
      )}
    </>
  );
};

export default ActiveTradePreview;

const StyledValue = styled(Text).attrs({
  as: "div",
})`
  font-weight: ${typography.fontWeights.SEMI_BOLD};
  font-size: 14px;
`;
const NoTradeText = styled(Text)`
  ${ActiveTradePreviewStyles.noTradeText};
  height: 100%;
`;
